import { useEffect, useState } from 'react';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { isPlatform } from '@ionic/react';
import { useEvent } from '@mindly/ui-components';
import useToast from '../../context/ToastContext/ToastContext';
import { useSelector } from 'react-redux';
import { internetConnectionSelector } from '../../redux/internetConnection/selector';
import { useTranslation } from 'react-i18next';
import { checkAppVersion } from '../../api/appVersions';
import { DateTime } from 'luxon';

const APP_SUPPORT_STATUS = 'APP_CHECK_KEY';
const LAST_APP_CHECK_KEY = 'LAST_APP_CHECK_KEY';
const REFRESH_INTERVAL_CHECK_KEY = 'REFRESH_INTERVAL_CHECK_KEY';

export function useAppUpdatesNotSupported() {
  const { t } = useTranslation('app');
  const internetConnection = useSelector(internetConnectionSelector);
  const [isAppSupported, setIsAppSupported] = useState(() => {
    return JSON.parse(window.localStorage.getItem(APP_SUPPORT_STATUS) ?? 'true');
  });
  const { showToast, hideToast } = useToast();

  const setAppSupportStatus = useEvent((value: boolean) => {
    window.localStorage.setItem(APP_SUPPORT_STATUS, JSON.stringify(value));
    setIsAppSupported(value);
  });

  const checkAppVersions = useEvent(async () => {
    if (!isPlatform('hybrid')) return;
    if (!internetConnection) return;

    try {
      const nativeUpdateInfo = await AppUpdate.getAppUpdateInfo();
      if (nativeUpdateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) return;

      const previousTime = Number(window.localStorage.getItem(LAST_APP_CHECK_KEY) ?? 0);
      const refreshInterval = Number(window.localStorage.getItem(REFRESH_INTERVAL_CHECK_KEY) ?? 3600);

      // Get the current time in seconds
      const currentTime = DateTime.utc().toSeconds();

      // Check if the difference between the current time and the previous time exceeds the refresh interval
      if (currentTime - previousTime < refreshInterval) {
        // If the interval hasn't passed, no need to refetch data
        return;
      }
      window.localStorage.setItem(LAST_APP_CHECK_KEY, `${currentTime}`);
      const { data, status } = await checkAppVersion();
      const isSupportedAppVersion = data?.is_app_version_supported && status === 201;
      window.localStorage.setItem(REFRESH_INTERVAL_CHECK_KEY, `${data?.refresh_interval}`);

      if (
        !isSupportedAppVersion &&
        nativeUpdateInfo.updateAvailability === AppUpdateAvailability.UPDATE_NOT_AVAILABLE
      ) {
        setAppSupportStatus(false);
      } else if (isSupportedAppVersion && data?.will_be_deleted) {
        setAppSupportStatus(true);
        showToast({
          text: t('old_device_warning.title'),
          buttons: [
            {
              text: t('old_device_warning.button'),
              handler: () => hideToast(),
            },
          ],
        });
      } else if (isSupportedAppVersion) {
        setAppSupportStatus(true);
      }
    } catch (error) {
      showToast({
        error,
      });
    }
  });

  useEffect(() => {
    checkAppVersions();
  }, [checkAppVersions]);

  return isAppSupported;
}
